import { Link, useLocation } from "@/lib/router-compat";
import { useEffect } from "react";
import { Helmet } from "@/lib/helmet-compat";
import { Button } from "@/components/ui/button";
import { ArrowRight, Home, Phone, Image as ImageIcon, MapPin, FileText } from "lucide-react";

const helpfulLinks = [
  { to: "/", icon: Home, title: "Home", desc: "Start over from the homepage" },
  { to: "/gallery", icon: ImageIcon, title: "Project Gallery", desc: "Browse real installs" },
  { to: "/locations", icon: MapPin, title: "Service Areas", desc: "See where we install" },
  { to: "/beat-my-estimate", icon: FileText, title: "Beat My Estimate", desc: "Send us a competitor quote" },
];

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  return (
    <>
      <Helmet>
        <title>Page Not Found | Modern Fence Install</title>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <section className="min-h-[70vh] flex items-center bg-offwhite">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-primary font-bold text-sm uppercase tracking-widest mb-3">Error 404</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-foreground mb-4 leading-none">
              We couldn't find that page.
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              The link may be broken or the page may have moved. Here are a few good places to head next.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center mb-12">
              <Button variant="cta" size="lg" className="px-8 py-6 gap-2" asChild>
                <Link to="/">Back to Home <ArrowRight className="w-5 h-5" /></Link>
              </Button>
              <Button size="lg" variant="outline" className="px-8 py-6 gap-2" asChild>
                <a href="tel:+18884163144"><Phone className="w-5 h-5" /> (888) 416-3144</a>
              </Button>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 text-left max-w-2xl mx-auto">
              {helpfulLinks.map(({ to, icon: Icon, title, desc }) => (
                <Link
                  key={to}
                  to={to}
                  className="group flex items-start gap-3 p-4 bg-background rounded-lg border border-border hover:border-primary/40 hover:shadow-md transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/15 transition-colors">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground group-hover:text-primary transition-colors">{title}</p>
                    <p className="text-sm text-muted-foreground">{desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default NotFound;
